import {
  Box,
  Chip,
  Typography,
  TypographyProps,
  useTheme,
} from '@mui/material';
import React, { ReactNode } from 'react';

import { hexToRGBA } from '../BarChart';

export interface SmallHeaderWithIconProps {
  icon: ReactNode;
  title: string;
  chipLabel?: string;
  chipColor?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'error'
    | 'info'
    | 'warning'
    | 'default';
  color?: string;
  iconSize?: 'small' | 'medium' | string;
  children?: ReactNode;
  fontWeight?: string | number;
  fontSize?: string;
  variant?: string;
  mb?: string | number;
}

const ICON_SIZE_MAP: Record<string, string> = {
  small: '1.25rem', // 20px, equivalente a MUI fontSize="small"
  medium: '1.5rem', // 24px, equivalente a MUI fontSize="medium"
};

const SmallHeaderWithIcon: React.FC<SmallHeaderWithIconProps> = ({
  icon,
  title,
  chipLabel,
  chipColor = 'primary',
  color = 'primary',
  iconSize = 'small',
  children,
  fontWeight = 700,
  fontSize = '1rem',
  variant = 'h1',
  mb = 2.5,
}) => {
  const theme = useTheme();
  const resolvedIconSize = ICON_SIZE_MAP[iconSize] ?? iconSize;

  const getBackgroundColor = () => {
    if (color.startsWith('#')) return hexToRGBA(color, 0.12);
    try {
      const themeColor = theme.palette[color as keyof typeof theme.palette];
      if (typeof themeColor === 'string') {
        return hexToRGBA(themeColor, 0.12);
      }
      if (
        themeColor &&
        typeof themeColor === 'object' &&
        'main' in themeColor
      ) {
        return hexToRGBA(themeColor.main, 0.12);
      }
    } catch (error) {
      console.error('Error getting theme color:', error);
    }
    return hexToRGBA(theme.palette.primary.main, 0.12);
  };

  const getIconColor = () => {
    if (color.startsWith('#')) return color;
    try {
      const themeColor = theme.palette[color as keyof typeof theme.palette];
      if (typeof themeColor === 'string') return themeColor;
      if (
        themeColor &&
        typeof themeColor === 'object' &&
        'main' in themeColor
      ) {
        return themeColor.main;
      }
    } catch (error) {
      console.error('Error getting theme color:', error);
    }
    return theme.palette.primary.main;
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: mb }}>
      <Box
        sx={{
          backgroundColor: getBackgroundColor(),
          borderRadius: '50%',
          p: 0.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: getIconColor(),
        }}
      >
        <Box
          sx={{
            fontSize: resolvedIconSize,
            display: 'flex',
            alignItems: 'center',
            '& svg': {
              fontSize: `${resolvedIconSize} !important`,
            },
          }}
        >
          {icon}
        </Box>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant={variant as TypographyProps['variant']}
          sx={{
            fontWeight: fontWeight,
            color: 'text.primary',
            fontSize: fontSize,
            lineHeight: 1.1,
          }}
        >
          {title}
        </Typography>
        {chipLabel && (
          <Chip
            label={chipLabel}
            size="small"
            color={chipColor}
            variant="outlined"
            sx={{ height: '18px', fontSize: '0.65rem' }}
          />
        )}
      </Box>
      {children}
    </Box>
  );
};

export default SmallHeaderWithIcon;
